// How sunlight casts shadows at a time of day. The sun crosses the sky, so shadows lie to the left in the morning
// (~200 degrees), point up the screen at noon (270) and lie to the right by evening (~340): beside things, where they
// show, rather than hidden behind them. Low sun: long, softer, warmer shadows; high sun: short, crisp, cooler, darker.
import { DayNight } from '../../../shared/src/dayNight';

export const Sun = {
  /** Shadow direction at dawn and dusk, degrees clockwise from right (90 = down, 270 = up). */
  dawnDirection: 200, duskDirection: 340,
  /** Shadow length, % of the caster's height: with the sun at the horizon, and at its highest. */
  lowDistance: 180, noonDistance: 35,
  /** Opacity with the sun low and at noon (scaled down only right at sunrise and sunset). */
  lowOpacity: 0.26, noonOpacity: 0.38,
  /** How much of the base's darkness is left at the shadow's tip: a long low-sun shadow fades more. */
  lowTip: 0.25, noonTip: 0.55,
  /** Shadow colours: a warm dusky violet with the sun low, a cool deep blue at noon. */
  lowColor: 0x3b2b4a, noonColor: 0x1b1d3d,
  /** The fraction of the day (dawn to dusk) over which shadows fade in after sunrise and out before sunset. */
  fade: 0.06,
};

export interface SunShadow {
  /** Degrees clockwise from right, and length as a % of the caster's height (whole numbers). */
  direction: number; distance: number;
  /** 0 (no shadows: night) .. 1, in hundredths; the tip's share of the base's darkness, in twentieths. */
  opacity: number; tip: number;
  /** 0xrrggbb. */
  color: number;
}

/** How far through the day (dawn 0 .. dusk 1) a time is; nights hold dusk's or dawn's. */
export function dayProgress(time: number): number {
  const t = DayNight.wrap(time);
  return Math.max(0, Math.min(1, (t - DayNight.dawn) / (DayNight.dusk - DayNight.dawn)));
}

const mix = (a: number, b: number, f: number) => a + (b - a) * f;
const smooth = (edge: number, x: number) => { const t = Math.max(0, Math.min(1, x / edge)); return t * t * (3 - 2 * t); };
const mixColor = (a: number, b: number, f: number) =>
  [16, 8, 0].reduce((c, shift) => c | (Math.round(mix((a >> shift) & 255, (b >> shift) & 255, f)) << shift), 0);

/** The sunlight shadow at a time of day, rounded so cached shadows redraw only as the sun moves on. */
export function sunShadow(time: number): SunShadow {
  const p = dayProgress(time), height = Math.sin(Math.PI * p); // the sun's height: 0 at the horizon, 1 at noon
  const strength = smooth(Sun.fade, p) * smooth(Sun.fade, 1 - p);
  return {
    direction: Math.round(mix(Sun.dawnDirection, Sun.duskDirection, p)),
    distance: Math.round(mix(Sun.lowDistance, Sun.noonDistance, height)),
    opacity: Math.round(strength * mix(Sun.lowOpacity, Sun.noonOpacity, height) * 100) / 100,
    tip: Math.round(mix(Sun.lowTip, Sun.noonTip, height) * 20) / 20,
    color: mixColor(Sun.lowColor, Sun.noonColor, Math.round(height * 10) / 10),
  };
}
